import React from 'react'
import { Formik } from 'formik'
import * as yup from 'yup'
import { Alert, Button, Form, Icon, Input } from 'antd'
import {
  CHARACTER_CLASS_RULES,
  HISTORY_SIZE,
  PASSWORD_RULES,
  characterClassesOf,
  passwordPolicyTest,
  passwordViolationMessages,
} from 'lib/passwordPolicy'

// Shared by the forced change on /logincpac (outside LangContext — Thai text
// is hardcoded on purpose) and the Header password-expiry modal.
// Presentational: the parent does the request and passes the result back.

export const POLICY_VIOLATION_TITLE = 'รหัสผ่านใหม่ไม่ตรงตามเงื่อนไข'

const MET_COLOR = '#52c41a'
const UNMET_COLOR = 'rgba(0, 0, 0, 0.35)'

export const changePasswordSchema = yup.object().shape({
  oldPassword: yup.string().required('กรุณากรอกรหัสผ่านเดิม'),
  newPassword: yup
    .string()
    .test(passwordPolicyTest())
    .required('กรุณากรอกรหัสผ่านใหม่'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('newPassword'), null], 'ยืนยันรหัสผ่านไม่ตรงกับรหัสผ่านใหม่')
    .required('กรุณายืนยันรหัสผ่านใหม่'),
})

const RuleLine = ({ met, label, indent, unmetColor = UNMET_COLOR }) => (
  <div
    className={`password-rule ${met ? 'password-rule-met' : 'password-rule-unmet'}`}
    style={{ color: met ? MET_COLOR : unmetColor, paddingLeft: indent ? 20 : 0, lineHeight: '20px' }}
  >
    {met ? (
      <Icon type='check-circle' theme='filled' style={{ marginRight: 6 }} />
    ) : (
      <span style={{ display: 'inline-block', width: 14, marginRight: 6, textAlign: 'center' }}>•</span>
    )}
    {label}
  </div>
)

// showHistoryRule=false: the user master create page — a new account has no
// password history (BE createUser checks the format rules only).
export const PasswordRuleChecklist = ({ password, showHistoryRule = true, unmetColor = UNMET_COLOR, style }) => {
  const classes = characterClassesOf(password)
  return (
    <div className='password-rule-checklist' style={{ fontSize: 12, marginBottom: 12, ...style }}>
      {PASSWORD_RULES.map((rule) => (
        <RuleLine key={rule.key} met={rule.isMet(password)} label={rule.label} unmetColor={unmetColor} />
      ))}
      {CHARACTER_CLASS_RULES.map((rule) => (
        <RuleLine key={rule.key} met={classes[rule.key]} label={rule.label} unmetColor={unmetColor} indent />
      ))}
      {showHistoryRule ? (
        <div className='password-rule-history' style={{ color: unmetColor, marginTop: 4 }}>
          {`ห้ามใช้รหัสผ่านซ้ำกับ ${HISTORY_SIZE} ครั้งล่าสุด (ตรวจตอนบันทึก)`}
        </div>
      ) : null}
    </div>
  )
}

const fieldStatus = (touched, errors, name) =>
  touched[name] && errors[name]
    ? { validateStatus: 'error', help: errors[name] }
    : {}

const ChangePasswordForm = ({
  username,
  onSubmit,
  onCancel,
  submitting = false,
  serverError = '',
  serverViolations = [],
  submitText = 'เปลี่ยนรหัสผ่าน',
  // red primary in the Header-banner popup; the forced change on /logincpac
  // passes false — a white default button like "ยกเลิก" (user 15/10/2026)
  primarySubmit = true,
}) => {
  const violationMessages = passwordViolationMessages(serverViolations)
  return (
    <Formik
      initialValues={{ oldPassword: '', newPassword: '', confirmPassword: '' }}
      validationSchema={changePasswordSchema}
      onSubmit={(values, actions) => {
        const done = () => actions.setSubmitting(false)
        Promise.resolve(
          onSubmit({ old_password: values.oldPassword, new_password: values.newPassword })
        ).then(done, done)
      }}
      render={({ values, errors, touched, handleSubmit, setFieldValue, setFieldTouched }) => (
        <Form className='change-password-form' onSubmit={handleSubmit}>
          {(serverError || violationMessages.length > 0) && (
            <Alert
              className='change-password-server-error'
              type='error'
              showIcon
              style={{ marginBottom: 16 }}
              message={serverError || POLICY_VIOLATION_TITLE}
              description={
                violationMessages.length > 0 ? (
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {violationMessages.map((text, i) => (
                      <li key={i}>{text}</li>
                    ))}
                  </ul>
                ) : null
              }
            />
          )}
          {username ? (
            <Form.Item label='ชื่อผู้ใช้' style={{ marginBottom: 8 }}>
              <Input name='username' value={username} disabled readOnly />
            </Form.Item>
          ) : null}
          <Form.Item label='รหัสผ่านเดิม' style={{ marginBottom: 8 }} {...fieldStatus(touched, errors, 'oldPassword')}>
            <Input.Password
              name='oldPassword'
              value={values.oldPassword}
              autoComplete='current-password'
              onChange={(e) => setFieldValue('oldPassword', e.target.value)}
              onBlur={() => setFieldTouched('oldPassword', true)}
            />
          </Form.Item>
          <Form.Item label='รหัสผ่านใหม่' style={{ marginBottom: 8 }} {...fieldStatus(touched, errors, 'newPassword')}>
            <Input.Password
              name='newPassword'
              value={values.newPassword}
              autoComplete='new-password'
              onChange={(e) => setFieldValue('newPassword', e.target.value)}
              onBlur={() => setFieldTouched('newPassword', true)}
            />
          </Form.Item>
          <PasswordRuleChecklist password={values.newPassword} />
          <Form.Item label='ยืนยันรหัสผ่านใหม่' style={{ marginBottom: 16 }} {...fieldStatus(touched, errors, 'confirmPassword')}>
            <Input.Password
              name='confirmPassword'
              value={values.confirmPassword}
              autoComplete='new-password'
              onChange={(e) => setFieldValue('confirmPassword', e.target.value)}
              onBlur={() => setFieldTouched('confirmPassword', true)}
            />
          </Form.Item>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            {onCancel ? (
              <Button className='change-password-cancel' style={{ marginRight: 8 }} onClick={onCancel} disabled={submitting}>
                ยกเลิก
              </Button>
            ) : null}
            <Button
              className='change-password-submit'
              type={primarySubmit ? 'primary' : undefined}
              htmlType='submit'
              loading={submitting}
            >
              {submitText}
            </Button>
          </div>
        </Form>
      )}
    />
  )
}

export default ChangePasswordForm
